import { useEffect, useState } from "react";

// Each panel owns its request and timer. Old task responses cannot populate a
// new selection, even if the transport finishes after it has been cancelled.
export default function useOrderBookPolling<T>(
  taskId: number | undefined,
  request: (taskId: string, signal?: AbortSignal) => Promise<T>,
  intervalMs = 5000,
) {
  const [state, setState] = useState<{
    taskId: number | undefined;
    response: T | null;
    loading: boolean;
    error: boolean;
  }>({ taskId, response: null, loading: !!taskId, error: false });

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController;
    setState({ taskId, response: null, loading: true, error: false });
    const poll = async () => {
      controller = new AbortController();
      try {
        const response = await request(String(taskId), controller.signal);
        if (!cancelled) setState({ taskId, response, loading: false, error: false });
      } catch {
        if (!cancelled) setState({ taskId, response: null, loading: false, error: true });
      } finally {
        if (!cancelled) timer = setTimeout(poll, intervalMs);
      }
    };
    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller?.abort();
    };
  }, [taskId, request, intervalMs]);

  return state.taskId === taskId && !!taskId
    ? state
    : { taskId, response: null, loading: !!taskId, error: false };
}
